import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";
import infoIcon from "../../assets/svg/info.svg";
import { getUsdcDecimals } from "../../config/chains";
import {
  AllCollaterals,
  DepositWithdrawCollaterals,
} from "../../constants/addresses";
import { ChainIdEnum, UNIFIED_VAULT_CHAIN_IDS } from "../../enums/chain";
import { useWithdrawalTransfer } from "../../hooks/api/withdrawTransfer/useWithdrawalTransfer";
import { useUnifiedVault } from "../../hooks/perpetual-dex/useUnifiedVault";
import { useToast } from "../../hooks/toast";
import useWallet, { ID_TO_CHAINS } from "../../hooks/wallet/useWallet";
import {
  collateralAssetDecimals,
  getCollateralAssets,
} from "../../utils/asset/assets";
import {
  supportedChainId,
  supportedSigningChainIds,
} from "../../utils/wallet/connectors";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import { Spinner } from "../shared/Spinner";
import {
  computeCollateralInfo,
  withdrawMaxResult,
  maxWithdrawValue,
  performWithdraw,
} from "./WithdrawModal.helpers";
import { IDepositWithdrawModalProps } from "./WithdrawModal.types";

export function useWithdrawModal({
  show,
  onHide,
  accountData,
  defaultAsset,
}: IDepositWithdrawModalProps) {
  const wallet = useWallet();
  const { chainId, setChainToSwitch } = wallet;
  const { addToast } = useToast();
  const { mutate } = useSWRConfig();

  const isUnifiedVaultChain = useMemo(
    () => Boolean(chainId && UNIFIED_VAULT_CHAIN_IDS.includes(chainId as ChainIdEnum)),
    [chainId]
  );
  const { t } = useTranslation("app", {
    keyPrefix: "DepositWithdrawModal.WithdrawModal",
  });
  const { t: formError } = useTranslation("formErrors");
  const { t: apiError } = useTranslation("apiErrors");
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    control,
    setValue,
    clearErrors,
    trigger,
  } = useForm({
    mode: "onChange",
  });

  const amount: string | undefined = useWatch({ control, name: "amount" });

  const [selectedWithdrawalChain, setSelectedWithrawalChain] = useState(
    supportedSigningChainIds[0]
  );

  const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
  const [asset, setAsset] = useState<AllCollaterals>(
    DepositWithdrawCollaterals.Usdc
  );
  // Resolve the SELECTED asset + its decimals so the direct on-chain withdraw
  // operates on the right token (not always USDC) with correct amount scaling.
  const assetDecimals = collateralAssetDecimals[asset] ?? getUsdcDecimals();
  const { withdraw: vaultWithdraw } = useUnifiedVault(
    wallet,
    asset,
    assetDecimals
  );
  const { createWithdrawal } = useWithdrawalTransfer(
    wallet,
    asset,
    selectedWithdrawalChain
  );

  const [showHelpInfo, setShowHelpInfo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notEnoughBridgeCapacityError, setNotEnoughBridgeCapacityError] =
    useState<string>();
  const [depositWithdrawError, setDepositWithdrawError] = useState<
    string | undefined
  >();

  // Clear any withdraw error as soon as the user edits the amount,
  // so a prior cancellation / failure message doesn't linger over the new input.
  useEffect(() => {
    if (depositWithdrawError) {
      setDepositWithdrawError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  const collateralInfo = computeCollateralInfo(accountData, asset);

  // When asset is not allowed, switch network and reset asset
  useEffect(() => {
    setSelectedWithrawalChain((chain) => {
      const allowedAssets = getCollateralAssets(chain, "withdraw");
      if (!allowedAssets.includes(asset)) {
        return supportedChainId;
      }
      return chain;
    });
  }, [asset]);

  // When chain is switched, reset asset
  useEffect(() => {
    const allowedAssets = getCollateralAssets(
      selectedWithdrawalChain,
      "withdraw"
    );
    if (defaultAsset && allowedAssets.includes(defaultAsset)) {
      setAsset(defaultAsset);
    } else {
      setAsset(allowedAssets[0]);
    }
  }, [defaultAsset, selectedWithdrawalChain]);

  // When chain is switched, set selected withdrawal chain
  useEffect(() => {
    if (chainId && supportedSigningChainIds.includes(chainId)) {
      setSelectedWithrawalChain(chainId);
    }
  }, [chainId]);

  // When selected withdrawal chain is changed, reset errors
  useEffect(() => {
    setNotEnoughBridgeCapacityError(undefined);
    setDepositWithdrawError(undefined);
  }, [selectedWithdrawalChain]);

  // Reset errors
  useEffect(() => {
    clearErrors();
    setDepositWithdrawError(undefined);
    setNotEnoughBridgeCapacityError(undefined);
    setShowHelpInfo(false);
  }, [clearErrors, show]);

  // One answer for the field, the MAX button and its reason.
  const withdrawMax = useMemo(
    () => withdrawMaxResult(accountData, collateralInfo, asset, assetDecimals),
    [accountData, asset, assetDecimals, collateralInfo]
  );
  const withdrawableBalance = withdrawMax.ok ? withdrawMax.max : undefined;

  const submitButtonContent = useMemo(() => {
    if (!isUnifiedVaultChain && chainId !== selectedWithdrawalChain) {
      return (
        <>
          <Button
            fullWidth
            buttonTheme={ButtonThemeEnum.HIGHLIGHT}
            type="button"
            onClick={() => setChainToSwitch(selectedWithdrawalChain)}
          >
            {t("switch_to", {
              chain: ID_TO_CHAINS[selectedWithdrawalChain as ChainIdEnum],
            })}
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            type={"button"}
            onClick={() => setShowHelpInfo(true)}
          >
            <img src={infoIcon} alt="info" />
          </Button>
        </>
      );
    }
    return (
      <>
        <Button
          fullWidth
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          type={"submit"}
          disabled={loading || !isValid}
        >
          {loading ? <Spinner /> : t("withdraw")}
        </Button>
        <Button
          buttonTheme={ButtonThemeEnum.NEUTRAL2}
          type={"button"}
          onClick={() => setShowHelpInfo(true)}
        >
          <img src={infoIcon} alt="info" />
        </Button>
      </>
    );
  }, [chainId, isUnifiedVaultChain, selectedWithdrawalChain, loading, isValid, setChainToSwitch, t]);

  // Unknown covers more than an unvalued account now: a WBTC/WETH row that
  // carries no value cannot turn the USD cap into tokens either.
  const withdrawableUnknown = !withdrawMax.ok;
  const withdrawableUnknownReason = withdrawMax.ok ? undefined : withdrawMax.reason;

  const onMaxClick = useCallback(() => {
    const max = maxWithdrawValue(withdrawableBalance, withdrawableUnknown);
    // Unknown withdrawable: Max is disabled, and must not fill in 0 either.
    if (max === undefined) return;
    clearErrors("amount");
    setValue("amount", max);
    trigger("amount");
  }, [clearErrors, setValue, trigger, withdrawableBalance, withdrawableUnknown]);

  const handleWithdraw = useCallback(
    () =>
      performWithdraw({
        setDepositWithdrawError,
        setNotEnoughBridgeCapacityError,
        setLoading,
        isUnifiedVaultChain,
        amount,
        assetDecimals,
        vaultWithdraw,
        createWithdrawal,
        mutate,
        onHide,
        addToast,
        t,
        apiError,
        asset,
        chainId,
      }),
    [addToast, amount, asset, chainId, collateralInfo, createWithdrawal, isUnifiedVaultChain, onHide, t, apiError, vaultWithdraw, wallet.chainId, mutate]
  );

  return {
    t,
    formError,
    showHelpInfo,
    setShowHelpInfo,
    asset,
    assetDecimals,
    setAsset,
    isUnifiedVaultChain,
    selectedWithdrawalChain,
    setSelectedWithrawalChain,
    assetDropdownOpen,
    setAssetDropdownOpen,
    onMaxClick,
    loading,
    register,
    errors,
    notEnoughBridgeCapacityError,
    withdrawableBalance,
    withdrawableUnknown,
    withdrawableUnknownReason,
    collateralInfo,
    depositWithdrawError,
    submitButtonContent,
    handleSubmit,
    handleWithdraw,
  };
}
